import { organizationListSchema, sparseOrganizationSchema, newOrganizationSchema, organizationIdSchema, organizationHandshakeRequestSchema, organizationHandshakeResponseSchema, handleInviteRequestSchema, respondToInviteByTokenRequestSchema, respondToInviteByTokenResponseSchema, inviteTokenInfoSchema, updateOrganizationNameSchema, } from '../contracts/organizations';
import { parseSchema } from '../errors';
import { organizationDashboardAlertsHistoryResponseSchema, organizationDashboardUsageHistoryResponseSchema, organizationDashboardTopAlertsResponseSchema, dashboardDefaultRangeResponseSchema, } from '../contracts/organization-dashboard';
import { apiKeyGenerationSchema, apiKeyStatusSchema, } from '../contracts/account';
import { pluginApiKeyListSchema, expirePluginApiKeyResponseSchema, } from '../contracts/plugin-api-keys';
import { slackSettingsResponseSchema, upsertSlackSettingsBodySchema, upsertSlackSettingsResponseSchema, } from '../contracts/slack-settings';
import { yaraExclusionsListSchema, addYaraExclusionBodySchema, addYaraExclusionResponseSchema, } from '../contracts/yara-exclusions';
import { extensionIdSchema } from '../contracts/extensions';
/**
 * List organizations for the current user
 */
export async function listUserOrganizations(client) {
    const response = await client.get('/organizations');
    return parseSchema(organizationListSchema, response);
}
/**
 * Get a specific organization by ID
 */
export async function getOrganization(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}`);
    return parseSchema(sparseOrganizationSchema, response);
}
/**
 * Update a specific organization by ID
 */
export async function updateOrganizationName(client, organizationId, name) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.put(`/organizations/${validatedId}`, { name });
    return parseSchema(updateOrganizationNameSchema, response);
}
/**
 * Create a new organization
 */
export async function createOrganization(client, organizationData) {
    const validatedData = parseSchema(newOrganizationSchema, organizationData);
    const response = await client.post('/organizations', validatedData);
    return parseSchema(sparseOrganizationSchema, response);
}
/**
 * Generate a new organization API key
 */
export async function generateOrganizationApiKey(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.post(`/account/organizations/${validatedId}/keys/generate`);
    return parseSchema(apiKeyGenerationSchema, response);
}
/**
 * Get API key status for organization
 */
export async function getOrganizationApiKeyStatus(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/account/organizations/${validatedId}/keys/status`);
    return parseSchema(apiKeyStatusSchema, response);
}
/**
 * Generate a new organization-level API key (for MDM installation)
 * Only owners can generate organization-level keys
 */
export async function generateOrganizationLevelApiKey(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.post(`/organizations/${validatedId}/keys/generate-organization-level`);
    return parseSchema(apiKeyGenerationSchema, response);
}
/**
 * Get organization-level API key status (for MDM installation)
 * Only owners can view this - shows key prefix to all owners
 */
export async function getOrganizationLevelApiKeyStatus(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/keys/status-organization-level`);
    return parseSchema(apiKeyStatusSchema, response);
}
/**
 * List all plugin API keys (Claude Desktop hook plugin) for an organization.
 * Returns active and expired keys; UI can show full history.
 * Owner-only — enforced by route auth.
 */
export async function listPluginApiKeys(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/plugin-api-keys`);
    return parseSchema(pluginApiKeyListSchema, response);
}
/**
 * Manually expire a plugin API key. Owner-only.
 */
export async function expirePluginApiKey(client, organizationId, keyId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.post(`/organizations/${validatedId}/plugin-api-keys/${keyId}/expire`);
    return parseSchema(expirePluginApiKeyResponseSchema, response);
}
/**
 * Returns the URL path that produces a Claude Desktop hook plugin zip download.
 * The frontend POSTs to this URL via fetch + blob; the response is binary,
 * so do NOT use the typed Axios helper (Zod parsing would corrupt the bytes).
 */
export function pluginDownloadPath(organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    return `/api/organizations/${validatedId}/plugin-api-keys/download`;
}
/**
 * Get organization Slack settings.
 * Returns null when no Slack settings are configured.
 */
export async function getOrganizationSlackSettings(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/settings/slack`);
    if (response === null || response === undefined) {
        return null;
    }
    return parseSchema(slackSettingsResponseSchema, response);
}
/**
 * Upsert organization Slack settings
 */
export async function upsertOrganizationSlackSettings(client, organizationId, data) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(upsertSlackSettingsBodySchema, data);
    const response = await client.put(`/organizations/${validatedId}/settings/slack`, validatedData);
    return parseSchema(upsertSlackSettingsResponseSchema, response);
}
/**
 * Delete organization Slack settings
 */
export async function deleteOrganizationSlackSettings(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    await client.delete(`/organizations/${validatedId}/settings/slack`);
}
/**
 * Get organization YARA excluded extensions
 */
export async function getOrganizationYaraExclusions(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/yara/organizations/${validatedId}/exclusions`);
    return parseSchema(yaraExclusionsListSchema, response);
}
/**
 * Add extension to organization YARA exclusion list
 */
export async function addOrganizationYaraExclusion(client, organizationId, data) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(addYaraExclusionBodySchema, data);
    const response = await client.post(`/yara/organizations/${validatedId}/exclusions`, validatedData);
    return parseSchema(addYaraExclusionResponseSchema, response);
}
/**
 * Remove extension from organization YARA exclusion list
 */
export async function removeOrganizationYaraExclusion(client, organizationId, extensionId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedExtensionId = parseSchema(extensionIdSchema, extensionId);
    await client.delete(`/yara/organizations/${validatedId}/exclusions/${encodeURIComponent(validatedExtensionId)}`);
}
/**
 * Get organization dashboard alerts history for a given time range
 */
export async function getOrganizationDashboardAlertsHistory(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/dashboard/alerts`, { params });
    return parseSchema(organizationDashboardAlertsHistoryResponseSchema, response);
}
/**
 * Get organization dashboard usage history (active users) for a given time range
 */
export async function getOrganizationDashboardUsageHistory(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/dashboard/usage`, { params });
    return parseSchema(organizationDashboardUsageHistoryResponseSchema, response);
}
/**
 * Get organization dashboard top alerts summary for a given time range.
 *
 * Returns aggregated alerts with distinct user counts per alert.
 */
export async function getOrganizationDashboardTopAlerts(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/dashboard/top-alerts`, { params });
    return parseSchema(organizationDashboardTopAlertsResponseSchema, response);
}
/**
 * KIR-5693: the range the dashboard's time-ranged widgets open on (`1d` for a
 * very busy organization, else `7d`).
 */
export async function getOrganizationDashboardDefaultRange(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const response = await client.get(`/organizations/${validatedId}/dashboard/default-range`);
    return parseSchema(dashboardDefaultRangeResponseSchema, response);
}
/**
 * Handle an organization invite (accept or reject)
 */
export async function handleInvite(client, organizationId, action) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedBody = parseSchema(handleInviteRequestSchema, { action });
    await client.post(`/account/organizations/${validatedId}/invite`, validatedBody);
}
/**
 * Resolve an invite token to org name + role (KIR-3786).
 * Used by the invite deep-link flow to render the approve modal.
 */
export async function getInviteInfoByToken(client, token) {
    const response = await client.get(`/account/invites/${encodeURIComponent(token)}`);
    return parseSchema(inviteTokenInfoSchema, response);
}
/**
 * Respond to an organization invite by its token (KIR-3786).
 * On accept the token decides the org; the authenticated user is placed into it.
 */
export async function respondToInviteByToken(client, token, action) {
    const validatedBody = parseSchema(respondToInviteByTokenRequestSchema, {
        token,
        action,
    });
    const response = await client.post('/account/invites/respond', validatedBody);
    return parseSchema(respondToInviteByTokenResponseSchema, response);
}
/**
 * Handshake endpoint for MDM installation (organization-level API key auth)
 */
export async function handshakeOrganization(client, organizationId, data) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedData = parseSchema(organizationHandshakeRequestSchema, data);
    const response = await client.post(`/account/organizations/${validatedId}/handshake`, validatedData);
    return parseSchema(organizationHandshakeResponseSchema, response);
}
/**
 * Delete an organization (owner-only)
 */
export async function deleteOrganization(client, organizationId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    await client.delete(`/organizations/${validatedId}`);
}
//# sourceMappingURL=organizations.js.map